import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { TextField } from './Fields'
import { SlimLayout } from './SlimLayout'
import Turnstile from './Turnstile'
import {
	requestPasswordReset,
	getCurrentUser,
} from '../../services/authService'

import { Logo } from '../LandingPage/Logo'

export const PasswordResetRequest = ({ sourceExtension, userToken }) => {
	const [email, setEmail] = useState('')
	const [message, setMessage] = useState('')
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [turnstileToken, setTurnstileToken] = useState('')
	const [turnstileResetSignal, setTurnstileResetSignal] = useState(0)
	const [turnstileStatus, setTurnstileStatus] = useState('loading')
	const [turnstileAttempt, setTurnstileAttempt] = useState(0)

	// Stable identity: Turnstile re-renders its widget when this callback changes
	const handleTurnstileStatus = useCallback(status => setTurnstileStatus(status), [])

	const user = getCurrentUser()

	useEffect(() => {
		if (user && user.email) {
			setEmail(user.email)
		}
	}, [user])

	const handleSubmit = async e => {
		e.preventDefault()
		setMessage('')
		setError('')

		// Block only once the widget is rendered and solvable. If it never
		// loaded, let the request through and let the server decide.
		if (turnstileStatus === 'ready' && !turnstileToken) {
			return setError('Please complete the verification check below.')
		}

		setLoading(true)

		try {
			await requestPasswordReset(email, turnstileToken)
			setMessage(
				'Reset link sent if the account exists. Check your spam folder if you don\'t see it.'
			)
		} catch (err) {
			setError('Failed to send password reset email. Please try again.')
		} finally {
			setLoading(false)
			// The token is spent on submit either way
			setTurnstileResetSignal(signal => signal + 1)
		}
	}

	return (
		<SlimLayout>
			{/* Go back button for signed in users - above logo */}
			{user && (
				<div className="mb-4">
					<Link
						to="/settings"
						className="inline-flex items-center text-sm text-bold text-blue-600 hover:text-blue-800"
					>
						← Go Back
					</Link>
				</div>
			)}

			<div className="flex">
				<>
					<Link to="/" aria-label="Home">
						<Logo
							color={'text-gray-600 text-lg'}
							height={'45px'}
							width={'45px'}
						/>
					</Link>
				</>
			</div>

			<h2 className="mt-3 mb-4 text-lg font-semibold text-gray-900">
				Reset Your Password
			</h2>
			<form onSubmit={handleSubmit} className="mt-1 grid grid-cols-1 gap-y-8">
				{message && <div className="text-green-500">{message}</div>}
				{error && <div className="text-red-500">{error}</div>}
				<TextField
					className="col-span-full -my-2"
					label="Email Address"
					id="email"
					name="email"
					type="email"
					required
					value={email}
					onChange={e => setEmail(e.target.value)}
				/>
				<Turnstile
					key={turnstileAttempt}
					onVerify={setTurnstileToken}
					onStatus={handleTurnstileStatus}
					resetSignal={turnstileResetSignal}
				/>

				{turnstileStatus === 'unavailable' && (
					<div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
						We couldn't load our security check. It's usually an ad blocker or a
						corporate network blocking{' '}
						<code className="text-xs">challenges.cloudflare.com</code>. Allow that
						domain and{' '}
						<button
							type="button"
							className="font-medium text-blue-600 hover:underline"
							onClick={() => {
								setTurnstileStatus('loading')
								setTurnstileAttempt(n => n + 1)
							}}
						>
							try again
						</button>
						.
					</div>
				)}

				<div>
					<button
						type="submit"
						disabled={loading}
						className="submit-button group inline-flex items-center justify-center py-2 px-4 text-sm font-semibold bg-blue-600 text-white hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus-visible:outline-blue-600 rounded-full w-full"
					>
						{loading && (
							<span className="loading loading-spinner loading-md mr-2"></span>
						)}
						Send Reset Link
					</button>
				</div>

				{!user && (
					<p className="mt-0 text-sm text-gray-700">
						Remembered your password?{' '}
						<Link
							to={`/account/login${
								sourceExtension ? '?source=extension&token=' + userToken : ''
							}`}
							className="font-medium text-blue-600 hover:underline"
						>
							Sign in
						</Link>
					</p>
				)}
			</form>
		</SlimLayout>
	)
}

export default PasswordResetRequest
